import { SearchableConversationFilterInput } from 'generated-graphql/types';
import { buildParticipantNameSearchClauses } from './buildParticipantNameSearchClauses';

/**
 * Builds the query behind the conversations tab search box.
 *
 * One box, two things it can mean. Recruiters type a name or they paste an ID
 * from their own system, and they should not have to tell us which, so both are
 * tried and a hit on either is enough.
 *
 * Typing more of a name narrows the list rather than widening it: "John Smith"
 * has to match both words, or searching for one John would return every John.
 *
 * Campaign name is deliberately not searchable — the conversation index holds
 * `campaignId`, not the title. See
 * docs/adr/0003-cross-campaign-conversations-list.md.
 */
export const buildConversationSearchClauses = ({
  searchText
}: {
  searchText: string;
}): SearchableConversationFilterInput[] => {
  const trimmed = searchText.trim();

  if (!trimmed) {
    return [];
  }

  const targets: SearchableConversationFilterInput[] = [];

  const nameClauses = buildParticipantNameSearchClauses(trimmed);
  if (nameClauses.length > 0) {
    targets.push({ and: nameClauses });
  }

  // matchPhrasePrefix so a partially typed ID still narrows the list; match so
  // a fully typed one still hits when the analyser has split it into tokens.
  targets.push({
    or: [
      { externalId: { match: trimmed } },
      { externalId: { matchPhrasePrefix: trimmed } }
    ]
  });

  return targets.length > 0 ? [{ or: targets }] : [];
};
